import OpenAI from 'openai';
import { 
  getCurrentAPIConfig, 
  API_PROVIDERS, 
  CURRENT_API_PROVIDER 
} from '../config/apiConfig';
import { logger } from '../utils/logger';

// Unified API Client for OpenAI and Gemini
class UnifiedAPIClient {
  constructor() {
    this.openaiClient = null;
    this.initialize();
  }

  initialize() {
    const config = getCurrentAPIConfig();
    
    logger.info('Initializing API client', { 
      provider: CURRENT_API_PROVIDER, 
      hasApiKey: !!config.apiKey 
    });
    
    if (CURRENT_API_PROVIDER === API_PROVIDERS.OPENAI && config.apiKey && config.apiKey.trim() !== '') {
      try {
        this.openaiClient = new OpenAI({
          apiKey: config.apiKey,
          dangerouslyAllowBrowser: true
        });
        logger.info('OpenAI client initialized successfully');
      } catch (error) {
        logger.error('Failed to initialize OpenAI client', { error: error.message });
        // Don't throw error, just log it and continue with demo mode
      }
    } else if (CURRENT_API_PROVIDER === API_PROVIDERS.GEMINI && config.apiKey) {
      // Gemini initialization would go here
      logger.info('Gemini client initialized');
    } else if (CURRENT_API_PROVIDER === API_PROVIDERS.DEMO) {
      logger.info('Demo mode - no API client needed');
    } else {
      logger.warn('No valid API configuration found, falling back to demo mode');
    }
  }

  isConfigured() {
    const config = getCurrentAPIConfig();
    return !!config.apiKey;
  }

  async generateCompletion(messages, options = {}) {
    const config = getCurrentAPIConfig();
    const {
      model = config.models.primary,
      temperature = config.temperature,
      maxTokens = config.maxTokens.step1
    } = options;

    logger.info('generateCompletion called', { 
      provider: CURRENT_API_PROVIDER, 
      hasOpenAIClient: !!this.openaiClient,
      messageCount: messages.length 
    });

    // Always use demo mode if no proper API setup or if OpenAI client failed to initialize
    if (CURRENT_API_PROVIDER === API_PROVIDERS.DEMO || 
        (CURRENT_API_PROVIDER === API_PROVIDERS.OPENAI && !this.openaiClient)) {
      logger.info('Using demo mode');
      return this.generateDemoCompletion(messages, { model, temperature, maxTokens });
    }

    if (CURRENT_API_PROVIDER === API_PROVIDERS.OPENAI && this.openaiClient) {
      return this.generateOpenAICompletion(messages, { model, temperature, maxTokens });
    } else if (CURRENT_API_PROVIDER === API_PROVIDERS.GEMINI) {
      return this.generateGeminiCompletion(messages, { model, temperature, maxTokens });
    } else {
      logger.warn('Falling back to demo mode due to unsupported provider');
      return this.generateDemoCompletion(messages, { model, temperature, maxTokens });
    }
  }

  async generateResponse(prompt, options = {}) {
    try {
      logger.info('generateResponse called', { 
        provider: CURRENT_API_PROVIDER,
        promptLength: prompt.length 
      });

      // Convert prompt to messages format for compatibility
      const messages = [{ role: 'user', content: prompt }];
      const response = await this.generateCompletion(messages, options);
      
      // Extract rating from response content
      const rating = this.extractRating(response.content);
      
      const result = {
        content: response.content,
        rating: rating,
        tokensUsed: response.tokensUsed || response.usage?.total_tokens || 0,
        provider: response.provider || CURRENT_API_PROVIDER
      };

      logger.info('generateResponse successful', { 
        provider: result.provider,
        rating: result.rating,
        tokensUsed: result.tokensUsed 
      });

      return result;
    } catch (error) {
      logger.error('generateResponse failed, using fallback demo response', { 
        provider: CURRENT_API_PROVIDER,
        error: error.message,
        stack: error.stack 
      });
      
      // Instead of throwing, return a fallback demo response
      const fallbackResponse = await this.generateDemoCompletion(
        [{ role: 'user', content: prompt }], 
        options
      );
      
      return {
        content: fallbackResponse.content,
        rating: this.extractRating(fallbackResponse.content),
        tokensUsed: fallbackResponse.tokensUsed || 0,
        provider: 'demo'
      };
    }
  }

  extractRating(content) {
    // Look for rating patterns in the response with decimal support
    const ratingPatterns = [
      /consensus rating:?\s*(\d+(?:\.\d+)?)/i,
      /overall rating:?\s*(\d+(?:\.\d+)?)/i,
      /final rating:?\s*(\d+(?:\.\d+)?)/i,
      /rating:?\s*(\d+(?:\.\d+)?)/i,
      /\*\*consensus rating:?\*\*:?\s*(\d+(?:\.\d+)?)/i,
      /\*\*overall rating:?\*\*:?\s*(\d+(?:\.\d+)?)/i,
      // More flexible patterns
      /consensus.*?(\d+(?:\.\d+)?)/i,
      /rating.*?(\d+(?:\.\d+)?)/i
    ];
    
    for (let i = 0; i < ratingPatterns.length; i++) {
      const pattern = ratingPatterns[i];
      const match = content.match(pattern);
      if (match) {
        const rating = parseFloat(match[1]); // Use parseFloat to handle decimals
        if (!isNaN(rating) && rating >= 1 && rating <= 5) {
          logger.info('Rating extracted successfully', { pattern: pattern.toString(), rating, match: match[0] });
          return rating;
        }
      }
    }
    
    // Log if no rating found for debugging
    logger.warn('No rating pattern matched', { contentPreview: content.substring(0, 200) });
    
    // Default to random rating between 2-4 for demo (with 0.5 increments)
    return CURRENT_API_PROVIDER === API_PROVIDERS.DEMO ? 
      (Math.floor(Math.random() * 5) + 4) / 2 : 3; // Generates 2.0, 2.5, 3.0, 3.5, 4.0
  }

  async generateOpenAICompletion(messages, options) {
    if (!this.openaiClient) {
      logger.error('OpenAI client not initialized, falling back to demo');
      return this.generateDemoCompletion(messages, options);
    }

    try {
      logger.info('Making OpenAI API call', { 
        model: options.model,
        messageCount: messages.length 
      });

      const response = await this.openaiClient.chat.completions.create({
        model: options.model,
        messages: messages,
        temperature: options.temperature,
        max_tokens: options.maxTokens
      });

      logger.info('OpenAI API call successful', { 
        model: options.model,
        tokensUsed: response.usage?.total_tokens || 0 
      });

      return {
        content: response.choices[0].message.content,
        usage: response.usage,
        tokensUsed: response.usage?.total_tokens || 0,
        model: options.model,
        provider: API_PROVIDERS.OPENAI
      };
    } catch (error) {
      logger.error('OpenAI API call failed, falling back to demo', { 
        error: error.message,
        stack: error.stack,
        model: options.model 
      });
      
      // Instead of throwing, fall back to demo mode
      return this.generateDemoCompletion(messages, options);
    }
  }

  async generateGeminiCompletion(messages, options) {
    // For now, we'll use a mock implementation
    // In a real implementation, you'd use the Gemini SDK
    logger.warn('Gemini API not yet implemented - using mock response');
    
    // Mock Gemini response
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
    
    const mockResponse = `**MOCK GEMINI RESPONSE**

This is a simulated Gemini API response. The actual Gemini integration would require:

1. Installing @google-ai/generativelanguage package
2. Implementing proper Gemini API calls
3. Converting message format to Gemini's expected format
4. Handling Gemini-specific response structure

**Academic Assessment:**
Based on the provided information, this would be a real Gemini evaluation.

**Demographic Profile:**
Gemini would analyze diversity factors here.

**Key Strengths:**
• Mock strength analysis
• Gemini's perspective on candidate

**Areas of Concern:**
• Mock areas for improvement
• Gemini's assessment of weaknesses

**OVERALL RATING: Medium**

*Note: This is a mock Gemini response. Implement actual Gemini SDK integration for real responses.*`;

    return {
      content: mockResponse,
      usage: { total_tokens: 500 }, // Mock usage
      tokensUsed: 500,
      model: options.model,
      provider: API_PROVIDERS.GEMINI
    };
  }

  async generateDemoCompletion(messages, options) {
    // Demo mode with realistic sample evaluations
    const demoResponses = [
      {
        content: `**Professor Williams:** "Looking at this candidate's profile, I have some concerns about the academic foundation. The GMAT score is below our median, and while diversity is important, we need to see stronger quantitative preparation."

**Director Patel:** "I agree about the GMAT concern, but let's consider the holistic picture. The undergraduate institution has a good reputation, and there might be other factors we're not seeing yet."

**Professor Williams:** "True, but we're competing for limited spots. The GPA seems reasonable, but combined with the test scores, I'm not seeing the academic rigor we typically look for."

**Director Patel:** "Fair point. The demographic background could add value to our class composition, but academic preparation is fundamental. Without stronger credentials, this would be an uphill battle."

**Consensus Rating: 2**

*This is a demo evaluation. Add your OpenAI API key for personalized AI-powered insights.*`,
      },
      {
        content: `**Professor Williams:** "This is a much stronger profile. The academic credentials are solid, and I can see clear preparation for our rigorous program."

**Director Patel:** "Absolutely. The test scores are competitive, the GPA demonstrates consistency, and the undergraduate background shows intellectual capability."

**Professor Williams:** "What I particularly like is the balance of quantitative preparation with diverse perspectives. This candidate would contribute meaningfully to class discussions."

**Director Patel:** "The only minor concern is making sure they can handle our intense curriculum, but the academic foundation suggests they're well-prepared."

**Consensus Rating: 4**

*This is a demo evaluation. Add your OpenAI API key for personalized AI-powered insights.*`,
      },
      {
        content: `**Professor Williams:** "This is an interesting case. The academic metrics are right around our average - not exceptional, but certainly competitive."

**Director Patel:** "I see solid preparation here. The scores meet our standards, and the academic progression shows steady growth and commitment."

**Professor Williams:** "The demographic profile adds some value to our diversity goals, though it's not dramatically under-represented. Overall, a solid candidate."

**Director Patel:** "I agree. Not a standout, but definitely someone who could succeed in our program. The foundation is there."

**Consensus Rating: 3**

*This is a demo evaluation. Add your OpenAI API key for personalized AI-powered insights.*`,
      }
    ];

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000));

    // Select a random demo response
    const response = demoResponses[Math.floor(Math.random() * demoResponses.length)];
    
    logger.info('Generated demo evaluation response');
    
    return {
      content: response.content,
      usage: { total_tokens: 300 + Math.floor(Math.random() * 200) },
      tokensUsed: 300 + Math.floor(Math.random() * 200),
      model: options.model,
      provider: API_PROVIDERS.DEMO
    };
  }

  async validateConnection() {
    try {
      const testMessages = [
        { role: 'user', content: 'Test connection' }
      ];
      
      const response = await this.generateCompletion(testMessages, {
        maxTokens: 10
      });
      
      return !!response.content;
    } catch (error) {
      logger.error('API connection validation failed', { 
        provider: CURRENT_API_PROVIDER,
        error: error.message 
      });
      return false;
    }
  }

  getProviderInfo() {
    const config = getCurrentAPIConfig();
    return {
      provider: CURRENT_API_PROVIDER,
      name: config.name,
      configured: this.isConfigured(),
      models: config.models
    };
  }
}

// Export singleton instance
export const unifiedAPIClient = new UnifiedAPIClient();

// Export for manual reinitialization if needed
export const reinitializeAPIClient = () => {
  unifiedAPIClient.initialize();
}; 